import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { useApolloClient } from '@apollo/client';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '../button/Button';
import EmailInput from '../input/EmailInput';
import PasswordInput from '../input/PasswordInput';
import AlertBanner from '../structure/AlertBanner';
import { loadIncompleteIntake } from '../../service/IncompleteIntakeService';
import { ButtonClass } from '../enums/ButtonClass';

const GuestRecover = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const params = useParams();
    const apolloClient = useApolloClient();
    const formState = searchParams.get('formState');
    const [alert, setAlert] = useState(<></>);

    useEffect(() => {
        if (!formState) {
            navigate(`/${params.coverage}/${params.party}/login`, { replace: true });
        }
    }, [formState, navigate, params.coverage, params.party]);

    if (!formState) {
        return null;
    }

    return (
        <Formik
            initialValues={{ incompleteIntakeEmailAddress: '', incompleteIntakePassword: '' }}
            onSubmit={() => { }}
        >
            {({ values, setValues, setFieldError, setFieldValue }) => (
                <div className="pds-u-padding-top-40 pds-u-padding-bottom-40">
                    <div className="pds-u-padding-bottom-24">
                        <h2>Regenerate Your Claim Link</h2>
                        <div>Due to a recent system upgrade, your previous link is no longer active. Your saved claim information remains secure and has not been lost.</div>
                        <div>Enter the email address and passkey used to save your progress, and we'll generate a new link for you to continue your claim.</div>
                    </div>
                    <div className="pds-u-padding-bottom-32" style={{ width: '260px' }}>
                        <Form>
                            <EmailInput id="incomplete-intake-email-address" label="Email address" required={true} />
                            <PasswordInput id="incomplete-intake-password" label="Passkey" />
                        </Form>
                    </div>
                    {alert}
                    <div className="pds-u-padding-bottom-80">
                        <Button
                            mainButtonClass={ButtonClass.PRIMARY}
                            buttonContent="Continue"
                            onClick={async () => {
                                try {
                                    setAlert(<></>);
                                    const currentEmail = values.incompleteIntakeEmailAddress ?? '';
                                    const currentPasskey = values.incompleteIntakePassword ?? '';

                                    const restoredValues = await loadIncompleteIntake(
                                        formState,
                                        currentPasskey,
                                        setValues,
                                        apolloClient.cache
                                    );

                                    await setValues({
                                        ...restoredValues,
                                        incompleteIntakeEmailAddress: currentEmail,
                                        incompleteIntakePassword: currentPasskey
                                    });
                                    setFieldValue('incompleteIntakeEmailAddress', currentEmail);
                                    setFieldValue('incompleteIntakePassword', currentPasskey);
                                    setFieldError('incompleteIntakePassword', undefined);

                                    const response = await fetch(`${process.env.REACT_APP_FORMS_SERVICE_URL}/generate-link`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                            email: values.incompleteIntakeEmailAddress,
                                            benefitType: restoredValues.benefit,
                                            party: restoredValues.party,
                                            claimId: restoredValues.claimId,
                                            formState,
                                            legacyRecovery: true
                                        })
                                    });

                                    if (!response.ok) {
                                        if (response.status === 401) {
                                            setAlert(
                                                <AlertBanner
                                                    variant="error"
                                                    message={<div>You have reached the limit for generating a new link. Please try again after some time.</div>}
                                                    hideDismissButton={true}
                                                />
                                            );
                                            return;
                                        }
                                        throw new Error('Legacy recovery generate-link request failed');
                                    }

                                    setFieldError('incompleteIntakePassword', undefined);
                                    setFieldValue('incompleteIntakeEmailAddress', currentEmail);
                                    setFieldValue('incompleteIntakePassword', currentPasskey);
                                    setAlert(
                                        <AlertBanner
                                            variant="success"
                                            message={<div>Successfully sent a new continue link to {currentEmail}.</div>}
                                            hideDismissButton={true}
                                        />
                                    );
                                } catch {
                                    setAlert(
                                        <AlertBanner
                                            variant="error"
                                            message={<div>Could not send a new link. Please verify your email address and passkey and try again.</div>}
                                            hideDismissButton={true}
                                        />
                                    );
                                }
                            }}
                        />
                    </div>
                </div>
            )}
        </Formik>
    );
};

export default GuestRecover;
